import type { Folder, FolderRole } from '@gandhi/mail-protocol';

export interface FolderNode {
  readonly folder: Folder;
  readonly children: readonly FolderNode[];
}

/** The order a mail client puts the standard folders in. Custom folders follow, by name. */
const ROLE_ORDER: readonly FolderRole[] = [
  'inbox',
  'drafts',
  'sent',
  'trash',
  'junk',
  'archive',
  'custom',
];

function compare(a: Folder, b: Folder): number {
  const byRole = ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
  return byRole === 0 ? a.name.localeCompare(b.name) : byRole;
}

/** Nests folders by `parentId`. A folder whose parent is missing is shown at the root. */
export function folderTree(folders: readonly Folder[]): FolderNode[] {
  const ids = new Set(folders.map((f) => f.id));
  const childrenOf = (parentId: string | undefined): FolderNode[] =>
    folders
      .filter((f) =>
        parentId === undefined
          ? f.parentId === undefined || !ids.has(f.parentId)
          : f.parentId === parentId,
      )
      .sort(compare)
      .map((folder) => ({ folder, children: childrenOf(folder.id) }));
  return childrenOf(undefined);
}

/** Empty-list wording, so a folder never reads as a failure to load. */
export function emptyFolderMessage(folder: Folder | undefined, searching: boolean): string {
  if (searching) return 'No conversations match this search.';
  switch (folder?.role) {
    case 'inbox':
      return 'Your inbox is empty.';
    case 'drafts':
      return 'No drafts.';
    case 'sent':
      return 'Nothing sent from this mailbox yet.';
    case 'trash':
      return 'Deleted items is empty.';
    case 'junk':
      return 'No junk mail.';
    case 'archive':
      return 'Nothing archived yet.';
    default:
      return 'This folder is empty.';
  }
}
